import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/views/settingsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import ResetPasswordTab from './subcomponents/ResetPasswordTab';
import OtpTab from './subcomponents/OtpTab';
import { NumbersService } from 'services/numbers/numbers';
import { finalize } from 'rxjs/operators';
import { setToast } from 'redux/modules/toast/actions';
import { dispatch } from 'redux/store';

// @ts-ignore
const useStyles = makeStyles(styles);

const MIN_PASSWORD_LENGTH = 8;
const MIN_OTP_LENGTH = 3;

interface IChangeAdslPasswordDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	adslLogin: string;
	phoneNumber: string;
}

const ResetAdslPasswordDialog = ({ hideDialog, isOpen, adslLogin, phoneNumber }: IChangeAdslPasswordDialogProps) => {
	const classes = useStyles();
	const [otp, setOtp] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [password, setPassword] = useState<string>('');
	const [confirmPassword, setConfirmPassword] = useState<string>('');
	const [error, setError] = useState<string>('');
	const [sendetOtp, setSendetOtp] = useState<boolean>(false);

	const sendOtp = useCallback(() => {
		setOtp('');
		setError('');
		setLoading(true);
		NumbersService.sendResetAdslPasswordOtp(adslLogin, phoneNumber)
			.pipe(
				finalize(() => {
					setLoading(false);
				})
			)
			.subscribe(() => {
				setSendetOtp(true);
			});
	}, [adslLogin, phoneNumber]);

	useEffect(() => {
		if (isOpen) {
			sendOtp();
		}
	}, [isOpen, sendOtp]);

	const rules = [
		{
			name: translate('passwordForm.rules.characters', MIN_PASSWORD_LENGTH),
			success: password.length >= MIN_PASSWORD_LENGTH,
		},
		{
			name: translate('passwordForm.rules.uppercase'),
			success: /[A-Z]+/.test(password),
		},
		{
			name: translate('passwordForm.rules.number'),
			success: /[0-9]+/.test(password),
		},
		{
			name: translate('passwordForm.rules.specialCharacter'),
			success: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password),
		},
	];

	const confirmButtonDisabled: boolean = useMemo(() => {
		return (
			!rules.every(({ success }) => success) ||
			confirmPassword !== password ||
			!(otp.length >= MIN_OTP_LENGTH) ||
			!sendetOtp
		);
	}, [rules, confirmPassword, password, otp, sendetOtp]);

	const resetPassword = () => {
		setLoading(true);
		NumbersService.confirmAdslResetPasswordOtp(password, otp, adslLogin, phoneNumber)
			.pipe(finalize(() => setLoading(false)))
			.subscribe(
				() => {
					hideDialog();
					dispatch(
						setToast({
							label: translate('number.settings.reset.adsl.password'),
							actionType: translate('success'),
						})
					);
					setPassword('');
					setConfirmPassword('');
					setOtp('');
				},
				({ response }) => {
					if (response?.code === 'ERROR_VALIDATING_OTP') {
						setError(translate('error.validating.otp'));
					} else {
						setError(translate('error.something.went.wrong.please.try.again'));
					}
				}
			);
	};

	return (
		<CustomDialog
			isOpen={isOpen}
			hideDialog={hideDialog}
			header={translate('number.settings.reset.adsl.password')}
			loading={loading}
			dialogBody={
				<div className={classes.resetAdslPasswordDialog}>
					<OtpTab
						resendDelay={1}
						otp={otp}
						setOtp={setOtp}
						sendOtp={sendOtp}
						error={error}
						inOneInput={true}
						submitOTP={resetPassword}
						resendText={translate('number.settings.resend.token')}
					/>
					<ResetPasswordTab
						setConfirmPassword={setConfirmPassword}
						confirmPassword={confirmPassword}
						setPassword={setPassword}
						password={password}
						rules={rules}
					/>
				</div>
			}
			rightSideButtons={[
				{
					text: translate('cancel'),
					color: 'tertiary-light',
					onClick: hideDialog,
				},
				{
					text: translate('reset'),
					color: 'primary-light',
					disabled: confirmButtonDisabled,
					onClick: resetPassword,
				},
			]}
		/>
	);
};

export default ResetAdslPasswordDialog;
